import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import * as XLSX from "xlsx";
import { CommonMethodService } from "../../common/commonMethod.service";
import { ResidentService } from "../../services/resident.service";
import { UserService } from "../../services/user.service";
@Component({
    selector: 'app-import-vehicle',
    templateUrl: './importVehicle.component.html',
    providers: [CommonMethodService, ResidentService, UserService]
})

export class ImportVehicleComponent implements OnInit, OnDestroy {
    subscribe: Array<Subscription> = [];
    loader: boolean = false;

    accountId: string;
    flatId: string;

    // Vehicle
    download: boolean = false;
    file: any;
    fileName: Array<string> = [];

    // Vehicle numbers already registered for this account/flat — used to
    // reject an uploaded sheet client-side instead of letting the backend
    // silently accept a duplicate (normalised: trimmed + uppercased so
    // "kag 420 x" and "KAG 420 X" are recognised as the same plate).
    existingVehicleNumbers: Set<string> = new Set();

    constructor(private toaster: ToastrService, private residentService: ResidentService, private activeRoute: ActivatedRoute,
        private route: Router, private commonMethod: CommonMethodService, private userService: UserService) { }
    ngOnInit(): void {
        this.accountId = sessionStorage.getItem('accountId');
        this.flatId = sessionStorage.getItem('flatId');
        this.loadExistingVehicleNumbers();
    }

    private loadExistingVehicleNumbers(): void {
        this.subscribe.push(this.residentService.getVehicleList(this.accountId, this.flatId).subscribe(
            data => {
                const list: any = JSON.parse(JSON.stringify(data)) || [];
                this.existingVehicleNumbers = new Set(
                    list
                        .map((vehicle: any) => this.normalizeVehicleNo(vehicle.vehicleNo))
                        .filter((vehicleNo: string) => vehicleNo !== '')
                );
            }, () => {
                // If this lookup fails we simply can't pre-screen against
                // already-registered vehicles — within-file duplicate
                // detection below still runs regardless.
            }));
    }

    private normalizeVehicleNo(value: any): string {
        return (value || '').toString().trim().toUpperCase();
    }

    // Finds the "Vehicle No" column in the sheet's header row. Matched
    // loosely (case/spacing-insensitive "vehicle" + "no"/"number") since the
    // exact header text comes from the backend-generated template, not this
    // codebase — falls back to the first column if nothing matches so a
    // renamed header doesn't silently skip validation.
    private findVehicleNoColumn(headerRow: any[]): number {
        const index = headerRow.findIndex((cell: any) =>
            /vehicle/i.test(String(cell || '')) && /no|number/i.test(String(cell || '')));
        return index >= 0 ? index : 0;
    }

    // Reads the selected sheet client-side and checks the Vehicle No column
    // for duplicates — both within the uploaded file itself, and against
    // vehicles already registered for this account — before it ever reaches
    // the backend. Resolves with the list of duplicate vehicle numbers found
    // (empty = safe to upload).
    private checkForDuplicateVehicleNos(file: File): Promise<string[]> {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
                    if (rows.length < 2) {
                        resolve([]);
                        return;
                    }
                    const columnIndex = this.findVehicleNoColumn(rows[0]);
                    const seenInFile: Set<string> = new Set();
                    const duplicates: Set<string> = new Set();
                    for (let i = 1; i < rows.length; i++) {
                        const vehicleNo = this.normalizeVehicleNo(rows[i][columnIndex]);
                        if (vehicleNo === '') {
                            continue;
                        }
                        if (seenInFile.has(vehicleNo) || this.existingVehicleNumbers.has(vehicleNo)) {
                            duplicates.add(vehicleNo);
                        }
                        seenInFile.add(vehicleNo);
                    }
                    resolve(Array.from(duplicates));
                } catch {
                    // Unreadable/unexpected file format — let the backend's
                    // own validation handle it rather than blocking upload.
                    resolve([]);
                }
            };
            reader.onerror = () => resolve([]);
            reader.readAsArrayBuffer(file);
        });
    }

    onFileChange(event) {
        if (event.target.files.length > 0) {
            this.file = event.target.files[0];
        }
    }
    // Onsubmit File
    onSubmit(): void {
        if (this.file === undefined) {
            this.toaster.error('Please select excel sheet');
            return;
        }
        this.loader = true;
        this.checkForDuplicateVehicleNos(this.file).then(duplicates => {
            if (duplicates.length > 0) {
                this.loader = false;
                const preview = duplicates.slice(0, 10).join(', ');
                const more = duplicates.length > 10 ? ` and ${duplicates.length - 10} more` : '';
                this.toaster.error('Duplicate vehicle no(s) found: ' + preview + more +
                    '. Vehicle numbers must be unique — please fix the sheet and try again.');
                return;
            }
            this.uploadFile();
        });
    }

    private uploadFile(): void {
        const formData = new FormData();
        formData.append('file', this.file);
        this.subscribe.push(this.residentService.importCommercialVehicle(formData, this.accountId, this.flatId).subscribe(
            data => {
                this.loader = false;
                const wrapper = JSON.parse(JSON.stringify(data));
                if (wrapper.Error > 0) {
                    this.download = true;
                    this.fileName = wrapper.url.split('.');
                }
                this.toaster.success(wrapper.Message);
                this.ngOnInit();
            }, error => {
                this.loader = false;
                this.commonMethod.returnErrorMessage(error);
            }));

    }



    //Download Sample Sheet 
    downloadSampleSheet(): void {
        this.loader = true;
        this.subscribe.push(this.residentService.downloadSampleSheet('vehicle.xlsx').subscribe(
            data => {
                this.loader = false;
                const file = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            }, error => {
                this.loader = false;
                const file = new Blob([error.error.text], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
                this.commonMethod.returnErrorMessage(error);
            }));
    }


    // Download Error Sheet
    downloadErrorSheet(): void {
        this.loader = true;
        this.subscribe.push(this.residentService.downloadErrorVehicleSheet(this.fileName[0]).subscribe(
          data => {
            this.loader = false;
            const file = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
    
          }, error => {
            this.loader = false;
            const file = new Blob([error.error.text], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            this.commonMethod.returnErrorMessage(error);
          }));
    
      }

    ngOnDestroy(): void {
        this.subscribe.forEach(destroyExistingValue => {
            destroyExistingValue.unsubscribe();
        });
    }

}
